<template>
  <div class="container">
    <!-- Sorting and Filtering Section -->
    <div class="filter-section">
      <!-- Sorting Options -->
      <div class="row mb-3">
        <div class="col-12 col-md-6">
          <label for="orderBy" class="form-label">Order By:</label>
          <select v-model="advancedFilters.sort.sortBy" class="form-select" @change="fetchSummaries">
            <option value="created_at">Created At</option>
            <option value="for_investment">For Investment</option>
            <option value="invest">Invest</option>
            <option value="available">Available</option>
          </select>
        </div>

        <div class="col-12 col-md-6">
          <label for="orderDirection" class="form-label">Order Direction:</label>
          <select v-model="advancedFilters.sort.sortDirection" class="form-select" @change="fetchSummaries">
            <option value="ASC">Ascending</option>
            <option value="DESC">Descending</option>
          </select>
        </div>
      </div>

      <!-- Date Range Filters -->
      <div class="row mb-3">
        <div class="col-12 col-md-6">
          <label for="startDate" class="form-label">Start Date:</label>
          <input type="date" v-model="advancedFilters.filter.startDate" class="form-control" @change="fetchSummaries" />
        </div>
        <div class="col-12 col-md-6">
          <label for="endDate" class="form-label">End Date:</label>
          <input type="date" v-model="advancedFilters.filter.endDate" class="form-control" @change="fetchSummaries" />
        </div>
      </div>
    </div>

    <!-- All Months Summary Displayed First -->
    <div class="all-months-summary mb-4">
      <h4>Summary for All Months</h4>
      <div class="row">
        <div class="col-3">Total Invested: {{ this.$helper.formatPrice(allMonthsSummary.totalInvest) }}</div>
        <div class="col-3">Total Available: {{ this.$helper.formatPrice(allMonthsSummary.totalAvailable) }}</div>
        <div class="col-3">Total For Investment: {{ this.$helper.formatPrice(allMonthsSummary.totalForInvestment) }}</div>
        <div class="col-3">Total Sell: {{ this.$helper.formatPrice(allMonthsSummary.totalSell) }}</div>
      </div>
    </div>

    <!-- Summary Cards -->
    <div v-for="(summary) in summarys" :key="summary.investment_summary_id" class="card mb-3" @click="changeCurrentSummaryId(summary)">
      <div class="card-body">
        <div class="row">
          <div class="col-3">{{ this.$helper.formatDate(summary.created_at, true) }}</div>
          <div class="col-3">For Investment: {{ this.$helper.formatPrice(summary.for_investment) }}</div>
          <div class="col-2" v-if="summary.sell_transaction">Sell: {{ this.$helper.formatPrice(summary.sell_transaction['sub_total']) }}</div>
          <div class="col-2" v-else>Sell: {{ this.$helper.formatPrice(0) }}</div>
          <div class="col-2">Invest: {{ this.$helper.formatPrice(summary.invest) }}</div>
          <div class="col-2">Available: {{ this.$helper.formatPrice(summary.available) }}</div>
        </div>

        <!-- Detailed Investment View (Hidden by Default) -->
        <div v-if="summaryInvestmentRows" class="investment d-none" :id="'collapse-' + summary.investment_summary_id">
          <div class="row">
            <div class="col-md-8">
              <Investment :row="summaryInvestmentRows"></Investment>
            </div>
            <div class="col-md-4">
              <InvestmentChart :row="summaryInvestmentRows"></InvestmentChart>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-12">
              <router-link class="card-link" target="_blank" :to="{ path: '/investment_plan/details', query: { id: summary.investment_summary_id }}">View More</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination Controls -->
    <div class="pagination-controls text-center mt-4">
      <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="btn btn-primary mx-2">Previous</button>
      <span>Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="btn btn-primary mx-2">Next</button>
    </div>
  </div>
</template>

<script>
import Investment from '@/views/investment_plan/summary/Investment.vue';
import InvestmentChart from '@/views/investment_plan/summary/Investment_chart.vue';

import gql from 'graphql-tag';

const SUMMARY_QUERY = gql`
  query Summary($advancedFilters: AdvancedFiltersInput) {
    summarys(advancedFilters: $advancedFilters) {
      investment_summary_id
      for_investment
      invest
      available
      created_at
      updated_at
      sell_transaction {
        sub_total
      }
    }
  }
`;

const ALL_MONTHS_SUMMARY_QUERY = gql`
  query AllMonthsSummary($advancedFilters: AdvancedFiltersInput) {
    allMonthsSummary(advancedFilters: $advancedFilters) {
      totalForInvestment
      totalInvest
      totalAvailable
      totalSell
    }
  }
`;


const TOTAL_SUMMARIES_QUERY = gql`
  query TotalSummaries($advancedFilters: AdvancedFiltersInput) {
    totalSummaries(advancedFilters: $advancedFilters)
  }
`;

const SUMMARY_INVESTMENT_ROWS_QUERY = gql`
  query SummaryInvestmentRows($investmentSummaryId: ID!) {
    summaryInvestmentRows(investment_summary_id: $investmentSummaryId) {
      investment_id
      investment_summary_id
      investment_type_id
      invest
      created_at
      investment_type {
        investment_type_id
        name
      }
    }
  }
`;

export default {
  name: 'investment-plan-summary',
  components: {
    Investment,
    InvestmentChart,
  },
  data() {
    return {
      summarys: [],
      allMonthsSummary: {
        totalInvest: 0,
        totalAvailable: 0,
        totalForInvestment: 0,
        totalSell: 0,
      },
      summaryInvestmentRows: [],
      currentSummaryId: '',
      currentPage: 1,
      pageSize: 10,
      totalSummaries: 0,
      totalPages: 0,
      advancedFilters: {
        sort: {
          sortBy: 'created_at',
          sortDirection: 'DESC',
        },
        filter: {
          startDate: '',
          endDate: '',
        },
        pagination: {
          page: 1,
          pageSize: 10,
        },
      },
    };
  },
  methods: {
    changeCurrentSummaryId(row) {
      document.querySelectorAll('.investment').forEach(el => el.classList.add('d-none'));
      const el = document.getElementById('collapse-' + row.investment_summary_id);
      if (el.classList.contains('d-none')) {
        el.classList.remove('d-none');
        el.classList.add('d-inline');
      } else {
        el.classList.remove('d-inline');
        el.classList.add('d-none');
      }
      this.currentSummaryId = row.investment_summary_id;
    },

    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.advancedFilters.pagination.page = this.currentPage; // Update pagination page
    },

    fetchSummaries() {
      const advancedFilters = {
        sort: this.advancedFilters.sort,
        filter: this.advancedFilters.filter,
        pagination: this.advancedFilters.pagination,
      };

      // Step 1: Fetch the total number of summaries to determine pagination
      this.$apollo.query({
        query: TOTAL_SUMMARIES_QUERY,
        variables: { advancedFilters },
        fetchPolicy: 'network-only', // Force a fresh network request each time
      })
        .then(response => {
          this.totalSummaries = response.data.totalSummaries;
          this.totalPages = Math.ceil(this.totalSummaries / advancedFilters.pagination.pageSize);
          return this.$apollo.query({
              query: SUMMARY_QUERY,
              variables: { advancedFilters },
              fetchPolicy: 'network-only',
            }).then(response => {
              this.summarys = response.data.summarys; // Update summaries data
              if (this.totalPages === 1) {
                this.calculateAllMonthsSummary(this.summarys); // Aggregate data
              } else {
                return this.$apollo.query({
                  query: ALL_MONTHS_SUMMARY_QUERY,
                  variables: { advancedFilters },
                  fetchPolicy: 'network-only',
                }).then(response => {
                  this.allMonthsSummary = response.data.allMonthsSummary;
                });
              }
          });
        })
        .catch(error => {
          console.error("Error determining total summaries:", error);
        });
    },
    calculateAllMonthsSummary(summaries) {
      let totalInvest = 0;
      let totalAvailable = 0;
      let totalForInvestment = 0;
      let totalSell = 0;

      // Aggregate the values for all months
      summaries.forEach(summary => {
        totalInvest += summary.invest;
        totalAvailable += summary.available;
        totalForInvestment += summary.for_investment;
        if (summary.sell_transaction) {
          totalSell += summary.sell_transaction.sub_total;
        }
      });

      // Set the allMonthsSummary object with aggregated values
      this.allMonthsSummary = {
        totalInvest,
        totalAvailable,
        totalForInvestment,
        totalSell,
      };
    },
  },
  watch: {
    currentPage() {
      this.fetchSummaries(); // Fetch summaries again when currentPage changes
    },
    'currentSummaryId': function () {
      this.$apollo.queries.summaryInvestmentRows.refetch(); // Refetch investment rows when the summary changes
    },
    'advancedFilters': {
      handler() {
        this.fetchSummaries(); // Refetch summaries when any filter changes
      },
      deep: true,
    },
  },
  apollo: {
    summaryInvestmentRows: {
      query: SUMMARY_INVESTMENT_ROWS_QUERY,
      variables() {
        return {
          investmentSummaryId: this.currentSummaryId,
        };
      },
    },
  },
  created() {
    this.fetchSummaries(); // Initial fetch when the component is created
  },
};
</script>

<style scoped>
/* Styling for Sorting and Filter Section */
.filter-section {
  margin-bottom: 20px;
}

.form-label {
  font-weight: 600;
}

.form-select, .form-control {
  padding: 8px;
  font-size: 1rem;
}

/* Styling for Pagination */
.pagination-controls button:disabled {
  opacity: 0.5;
}

.pagination-controls button {
  margin: 0 10px;
  padding: 10px 20px;
  background-color: var(--primary);
  color: white;
  border: none;
}

.pagination-controls button:hover {
  background-color: #1D4ED8;
}

/* Styling for All Months Summary */
.all-months-summary {
  background-color: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 15px 20px;
  margin-bottom: 20px;
}

.all-months-summary h4 {
  font-weight: 600;
}

.all-months-summary .row {
  margin-bottom: 10px;
}

/* Card styling */
.card {
  cursor: pointer;
  border-radius: 12px;
  border: 1px solid var(--border);
}

.card-body {
  padding: 15px;
}

.card-body .row {
  margin-bottom: 10px;
}

.card-body .col-3, .card-body .col-2 {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .col-md-6 {
    margin-bottom: 15px;
  }

  .card-body .row {
    flex-direction: column;
  }

  .pagination-controls button {
    padding: 8px 15px;
  }
}
</style>